/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { AsyncResult } from '@msdyn365-commerce/core';
import MsDyn365 from '@msdyn365-commerce/core';
import { Customer } from '@msdyn365-commerce/retail-proxy';
import { IHeaderViewProps } from '@msdyn365-commerce-modules/header';
import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { Collapse, Drawer, Module, Node } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as React from 'react';

import { IHeaderProps } from '../definition-extensions/header.ext.props.autogenerated';

/**
 * GridSizes.
 */
export type GridSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

/**
 *
 * HeaderView component.
 * @extends {React.PureComponent<IHeaderViewProps>}
 */
export class HeaderView extends React.PureComponent<IHeaderViewProps & IHeaderProps<{}>> {
    public constructor(props: IHeaderViewProps & IHeaderProps<{}>) {
        super(props);
    }

    public componentDidMount(): void {
        if (this.props.config.useStickyHeader) {
            // Set header update methods to trigger on scroll
            if (MsDyn365.isBrowser) {
                window.addEventListener('scroll', () => {
                    this._configureStickyHeader();
                });
            }
            this._configureStickyHeader();
        }
    }

    public render(): JSX.Element | null {
        // eslint-disable-next-line @typescript-eslint/naming-convention -- Cannot change the prop names as props are from business logic.
        const {
            HeaderTag,
            HeaderContainer,
            HeaderTopBarContainer,
            Divider
        } = this.props;
        return (
          <>
            <Module {...HeaderTag}>
                <Node {...HeaderContainer}>
                    <Node {...HeaderTopBarContainer}>
                        {this.props.navIcon}
                        {this.props.logo}
                        {this._renderReactFragment(this.props.search)}
                        {this.props.preferredStore}
                        {this._renderAccountBlock(this.props, false)}
                        <Node {...Divider} />
                        {this.props.cartIcon}
                        {this._renderReactFragment(this.props.siteOptions)}
                    </Node>
                    {this._renderCollapseMenu(this.props)}
                    <Node className='ms-header__desktop-view'>
                        {this._renderReactFragment(this.props.menuBar)}
                    </Node>
                </Node>
            </Module>
            {this._renderReactFragment(this.props.slots.pastDueNotice)}
              </>
        );
    }

    /**
     * Function to render account block.
     * @param props - IHeaderViewProps.
     * @returns Returns JSX.Element | null.
     */
    private _renderCollapseMenu(props: IHeaderViewProps): JSX.Element | null {
        const { Divider, MobileMenuLinksContainer, mobileMenuCollapsed } = props;
        return (
            <Collapse className='ms-header__collapsible-hamburger' isOpen={!mobileMenuCollapsed}>
                <Node {...MobileMenuLinksContainer}>
                    {this._renderMobileAccountBlock(props, true)}
                    {props.wishListIconMobile}
                    {props.siteOptions}
                </Node>
                <Node {...Divider} />
                {this._renderReactFragment(props.menuBar)}
            </Collapse>
        );
    }

    private _renderMobileAccountBlock(props: IHeaderViewProps, renderForMobile: boolean): JSX.Element | null {
        const { AccountInfoDropdownParentContainer, signOutLink, signInLink, data, accountLinks } = props;

        if (AccountInfoDropdownParentContainer) {
            const accountClassName = classnames(
                'ms-header__drawer',
                AccountInfoDropdownParentContainer.className,
                renderForMobile ? 'account-mobile' : 'account-desktop'
            );

            if (accountLinks) {
                return (
                    <Drawer
                        className={accountClassName}
                        openGlyph='ms-header__drawer-open'
                        closeGlyph='ms-header__drawer-close'
                        glyphPlacement='end'
                        toggleButtonText={this.renderCustomerName(data.accountInformation)}
                    >
                        <div>
                            {accountLinks ? accountLinks.map((link: React.ReactNode) => link) : false}
                            {signOutLink}
                        </div>
                    </Drawer>
                );
            } else if (signInLink) {
                return (
                    <Node {...AccountInfoDropdownParentContainer} className={accountClassName}>
                        {signInLink}
                    </Node>
                );
            }
        }
        return null;
    }

    private renderCustomerName = (accountInformation: AsyncResult<Customer>): React.ReactChild => {
        const customer = accountInformation && accountInformation.result;
        return <>{customer ? customer.FirstName || customer.Name : ''}</>;
    };

    /**
     * Function to update header.
     */
    private readonly _configureStickyHeader = (): void => {
        // Get heights of cookie and promotion banners
        const defaultValue = 0;
        const headerAlertsContainer: HTMLElement | null = document.querySelector('.ms-promo-banner');
        const bannerHeights = headerAlertsContainer ? headerAlertsContainer.offsetHeight : defaultValue;

        // Triggers opacity change of header
        const headerElement = document.querySelector('header .default-container');
        if (headerElement) {
            if (MsDyn365.isBrowser && document.documentElement.scrollTop > defaultValue) {
                headerElement.classList.add('lock-opaque');
                headerElement.classList.add('fixed');
            } else {
                headerElement.classList.remove('lock-opaque');
                headerElement.classList.remove('fixed');
            }
        }

        // Update sticky header position and opacity
        const stickyHeader: HTMLElement | null = document.querySelector('.desktop-navicon-container');
        const headerLogo: HTMLElement | null = document.querySelector('.ms-header__logo');

        if (stickyHeader && headerLogo) {
            // Fix center sticky header
            const navStickyPos = headerLogo.offsetHeight + bannerHeights;
            if (MsDyn365.isBrowser && document.documentElement.scrollTop > navStickyPos) {
                stickyHeader.classList.add('fixed');
            } else {
                stickyHeader.classList.remove('fixed');
            }
        }
    };

    private _renderAccountBlock(props: IHeaderViewProps, renderForMobile: boolean): JSX.Element | null {
        const {
            AccountInfoDropdownParentContainer,
            AccountInfoDropdownPopoverConentContainer,
            accountInfoDropdownButton,
            signOutLink,
            signInLink,
            accountLinks
        } = props;

        if (AccountInfoDropdownParentContainer) {
            const accountClassName = classnames(
                AccountInfoDropdownParentContainer.className,
                renderForMobile ? 'account-mobile' : 'account-desktop'
            );
            if (AccountInfoDropdownPopoverConentContainer) {
                return (
                    <Node {...AccountInfoDropdownParentContainer} className={accountClassName}>
                        {accountInfoDropdownButton}
                        <Node {...AccountInfoDropdownPopoverConentContainer}>
                            {accountLinks ? accountLinks.map((link: React.ReactNode) => link) : false}
                            {signOutLink}
                        </Node>
                    </Node>
                );
            } else if (signInLink) {
                return (
                    <Node {...AccountInfoDropdownParentContainer} className={accountClassName}>
                        {signInLink}
                    </Node>
                );
            }
        }

        return null;
    }

    /**
     * Function to render react fragment.
     * @param items - React nodes.
     * @returns Returns JSX.Element | null.
     */
    private readonly _renderReactFragment = (items: React.ReactNode[]): JSX.Element | null => {
        return (
            <>
                {ArrayExtensions.hasElements(items)
                    ? items.map((slot: React.ReactNode) => {
                          return <>{slot}</>;
                      })
                    : null}
            </>
        );
    };
}

export default HeaderView;
